import {
  Col,
  Row,
  Space,
  Table,
  Spin,
  Card,
  message,
  Modal,
  Button,
  Tooltip,
  Tabs,
} from "antd";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import requets from "../../api";
import columnAlert from "./table/data.table";
import ReportExcel from "../../api/service/report/reportExcel";
import { ReportCsv } from "../../api/service/report/reportCsv";
import { useAppAuth } from "../../context/use-app-auth";
import { useTranslation } from "react-i18next";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import AlertInactive from "./alert.inactive";

export default function Alert() {
  const auth = useAppAuth();
  const { t } = useTranslation("global");
  const controlPanel = useSelector((state) => state.controlPanel);

  const req = useMemo(
    () => requets(auth?.user?.access_token),
    [auth?.user?.access_token],
  );

  const [data, setData] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  const filteredData = useMemo(() => {
    const isLookingForActive = activeTab === "1";
    return data.filter((item) => item.isActive === isLookingForActive);
  }, [data, activeTab]);

  const fetchAlerts = useCallback(async () => {
    try {
      setSpinning(true);

      const paramsRaw = {
        devEui: controlPanel?.devEui,
        status: controlPanel?.status,
        state: controlPanel?.state,
        description: controlPanel?.description,
        isActive: activeTab === "1",
        deviceName: controlPanel?.deviceName,
        locationName: controlPanel?.locationName,
        countryName: controlPanel?.countryName,
        departmentName: controlPanel?.departmentName,
        countyName: controlPanel?.countyName,
      };

      const params = Object.fromEntries(
        Object.entries(paramsRaw).filter(
          ([_, value]) => value !== undefined && value !== null && value !== "",
        ),
      );

      const response = await req.get("/alerts", params);
      const apiData = response?.data?.data || response?.data || [];

      const formattedData = apiData
        .map((item) => ({
          ...item,
          date: item.createdAt,
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setData(formattedData);
    } catch (error) {
      console.error("ERROR:", error?.response || error);
      message.error(t("alerts.loadingError"));
    } finally {
      setSpinning(false);
    }
  }, [controlPanel, req, t, activeTab]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const toggleActive = async (record) => {
    try {
      setSpinning(true);

      setData((prevData) =>
        prevData.filter((item) => item.uuid !== record.uuid),
      );

      await req.patch(`/alerts/${record.uuid}`, {
        isActive: !record.isActive,
      });

      message.success(t("alerts.updatedStatus"));
      fetchAlerts();
    } catch (error) {
      console.error(error);
      message.error(t("alerts.updateError"));
      fetchAlerts();
    } finally {
      setSpinning(false);
    }
  };

  const deleteAlert = (record) => {
    Modal.confirm({
      title: t("alerts.deleteTitle"),
      content: t("alerts.deleteContent"),
      okText: t("global.yes"),
      cancelText: t("global.no"),
      okButtonProps: {
        type: "primary",
        className: "alert-confirm-ok-btn",
      },
      cancelButtonProps: {
        className: "alert-confirm-cancel-btn",
      },
      onOk: async () => {
        try {
          setSpinning(true);
          await req.delete(`/alerts/${record.uuid}`);
          message.success(t("alerts.deletedSuccess"));
          fetchAlerts();
        } catch (error) {
          console.error(error);
          message.error(t("alerts.deleteError"));
        } finally {
          setSpinning(false);
        }
      },
    });
  };

  const translate = useCallback(
    (rows) =>
      rows.map((item) => {
        const raw = {
          date: item.date ? dayjs(item.date).format("YYYY-MM-DD HH:mm") : "-",
          eui: item.devEui,
          deviceName: item.deviceName,
          description: item.description,
          status: item.status,
          state: item.state,
          active: item.isActive ? "TRUE" : "FALSE",
          locationName: item.locationName,
          countryName: item.countryName,
          departmentName: item.departmentName,
          countyName: item.countyName,
        };

        const out = {};
        for (const key in raw) {
          out[t(`alerts.tableColumn.${key}`)] = raw[key];
        }
        return out;
      }),
    [t],
  );

  const exportFileName = `${
    activeTab === "1" ? "Alertas_Activas" : "Alertas_Inactivas"
  }-${dayjs().format("YYYY-MM-DD")}`;

  const tabBarExtra = (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Tooltip title={t("alerts.reload")}>
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={fetchAlerts}
          className="square-btn"
        />
      </Tooltip>
    </div>
  );

  return (
    <Spin spinning={spinning} tip={t("alerts.loading")}>
      <style>
        {`
        /* BOTONES DE ACCIÓN EN TABLA */
        .square-btn {
          width: 40px !important;
          height: 40px !important;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 8px !important;
        }

        .square-btn .anticon {
          display: flex !important;
          align-items: center;
          justify-content: center;
        }

        .square-btn .anticon svg {
          margin: auto;
        }

        /* ESTILOS MODAL CONFIRMACIÓN */
        .ant-modal-confirm-btns {
          display: flex !important;
          justify-content: center !important;
          gap: 12px !important;
          margin-top: 16px !important;
          width: 100% !important;
        }

        .ant-modal-confirm-btns button {
          width: 40px !important;
          height: 40px !important;
          min-width: 40px !important;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 6px !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }

        /* POPCONFIRM (BOTONES NO / SÍ) */
        .ant-popconfirm-buttons {
          display: flex !important;
          justify-content: center !important;
          gap: 12px !important;
          width: 100% !important;
          margin-top: 12px !important;
        }

        .ant-popconfirm-buttons .ant-btn {
          width: 40px !important;
          height: 40px !important;
          min-width: 40px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 8px !important;
        }

        /* BOTÓN "SÍ" EXACTAMENTE IGUAL A DEVICE.JSX */
        .alert-confirm-ok-btn {
          background-color: #1677ff !important;
          border-color: #1677ff !important;
          color: #ffffff !important;
        }

        .alert-confirm-ok-btn:hover {
          background-color: #4096ff !important;
          border-color: #4096ff !important;
          color: #ffffff !important;
        }

        /* BOTÓN "NO" */
        .alert-confirm-cancel-btn {
          border-color: #d9d9d9 !important;
        }

        /* BOTÓN ELIMINAR */
        .btn-outline-red {
          border-color: #ff4d4f !important;
          color: #ff4d4f !important;
        }

        .btn-outline-red:hover {
          border-color: #ff7875 !important;
          background-color: #fff1f0 !important;
          color: #ff7875 !important;
        }
        `}
      </style>

      <div className="layout-content">
        <Row gutter={[24, 0]}>
          <Col span={24}>
            <Card bordered={false} className="header-solid h-full ant-card-p-0">
              <Tabs
                defaultActiveKey="1"
                onChange={(key) => setActiveTab(key)}
                style={{ padding: "0 24px" }}
                destroyInactiveTabPane
                tabBarExtraContent={tabBarExtra}
                items={[
                  {
                    key: "1",
                    label: t("alerts.activeAlerts"),
                    children: (
                      <div style={{ padding: "0px 0 24px" }}>
                        <Row justify="end" style={{ marginBottom: 16 }}>
                          <Col>
                            <Space wrap>
                              <ReportExcel
                                dataSet={translate(filteredData)}
                                fileName={exportFileName}
                              />
                              <ReportCsv
                                dataSet={translate(filteredData)}
                                fileName={exportFileName}
                              />
                            </Space>
                          </Col>
                        </Row>
                        <Table
                          columns={columnAlert(toggleActive, deleteAlert, t)}
                          dataSource={filteredData}
                          rowKey="uuid"
                          scroll={{ x: "max-content" }}
                          pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) =>
                              `${t("alerts.total") || "Total"}: ${total}`,
                          }}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "2",
                    label: t("alerts.inactiveAlerts"),
                    children: (
                      <AlertInactive
                        data={filteredData}
                        toggleActive={toggleActive}
                        deleteAlert={deleteAlert}
                        translate={translate}
                        t={t}
                        exportFileName={exportFileName}
                      />
                    ),
                  },
                ]}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </Spin>
  );
}
